import React, {useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {FiMap, FiTruck} from 'react-icons/fi';
import {useNavigate} from 'react-router-dom';
import {login as loginCustomer, signUp} from '../../api/api';

const logo = '/logo192.png';

export default function Register() {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async e => {
    e?.preventDefault?.();

    const cleanFirstName = firstName.trim();
    const cleanLastName = lastName.trim();
    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = phone.trim();

    if (!cleanFirstName || !cleanLastName || !cleanEmail || !password || !confirmPassword) {
      setError('Please fill all required fields.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await signUp({
        email: cleanEmail,
        password,
        firstName: cleanFirstName,
        lastName: cleanLastName,
        phone: cleanPhone,
      });

      const data = await loginCustomer({
        email: cleanEmail,
        password,
      });

      const token = data?.token?.token || data?.token;
      const user = data?.user;

      if (token) {
        localStorage.setItem('token', token);
      }

      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }

      navigate('/profile');
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={css(styles.page)}>
      <div className={css(styles.shell)}>
        <section className={css(styles.heroCard)}>
          <div className={css(styles.logoBadge)}>
            <img
              src={logo}
              alt="YaBatho Logo"
              className={css(styles.logoImage)}
            />
          </div>

          <p className={css(styles.kicker)}>YaBatho Customer</p>

          <h1 className={css(styles.heroTitle)}>Create your account</h1>

          <p className={css(styles.heroText)}>
            Join YaBatho to order from local outlets, manage your profile, and
            track your deliveries from one secure customer account.
          </p>

          <div className={css(styles.featureGrid)}>
            <div className={css(styles.featureCard)}>
              <span className={css(styles.featureIcon)} aria-hidden="true"><FiMap /></span>

              <div>
                <strong className={css(styles.featureTitle)}>
                  Local outlets
                </strong>
                <p className={css(styles.featureText)}>
                  Discover restaurants near you.
                </p>
              </div>
            </div>

            <div className={css(styles.featureCard)}>
              <span className={css(styles.featureIcon)} aria-hidden="true"><FiTruck /></span>

              <div>
                <strong className={css(styles.featureTitle)}>
                  Delivery updates
                </strong>
                <p className={css(styles.featureText)}>
                  Follow your order journey clearly.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={css(styles.card)}>
          <div className={css(styles.formHeader)}>
            <p className={css(styles.formKicker)}>Customer Registration</p>
            <h2 className={css(styles.title)}>Register</h2>
            <p className={css(styles.subtitle)}>
              Create your YaBatho customer account below.
            </p>
          </div>

          <form onSubmit={handleRegister} className={css(styles.form)}>
            {error ? (
              <div className={css(styles.errorBox)}>
                <span className={css(styles.errorIcon)}>!</span>
                <p className={css(styles.error)}>{error}</p>
              </div>
            ) : null}

            <div className={css(styles.nameGrid)}>
              <div className={css(styles.fieldGroup)}>
                <label className={css(styles.label)}>First Name</label>
                <input
                  className={css(styles.input)}
                  type="text"
                  placeholder="Enter first name"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                />
              </div>

              <div className={css(styles.fieldGroup)}>
                <label className={css(styles.label)}>Last Name</label>
                <input
                  className={css(styles.input)}
                  type="text"
                  placeholder="Enter last name"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                />
              </div>
            </div>

            <label className={css(styles.label)}>Email Address</label>
            <input
              className={css(styles.input)}
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />

            <label className={css(styles.label)}>Phone Number</label>
            <input
              className={css(styles.input)}
              type="tel"
              placeholder="Enter phone number"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />

            <label className={css(styles.label)}>Password</label>
            <input
              className={css(styles.input)}
              type="password"
              placeholder="Create password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />

            <label className={css(styles.label)}>Confirm Password</label>
            <input
              className={css(styles.input)}
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />

            <button
              type="submit"
              className={css(styles.button, loading && styles.buttonDisabled)}
              disabled={loading}>
              {loading ? 'Registering...' : 'Create Account'}
            </button>

            <div className={css(styles.links)}>
              <span className={css(styles.switchText)}>
                Already have an account?
              </span>

              <button
                type="button"
                className={css(styles.linkButton)}
                onClick={() => navigate('/')}>
                Login
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#FFFDF7',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px',
    boxSizing: 'border-box',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
  },

  shell: {
    width: '100%',
    maxWidth: '1080px',
    display: 'grid',
    gridTemplateColumns: '0.92fr 1fr',
    gap: '22px',
    alignItems: 'stretch',

    '@media (max-width: 860px)': {
      gridTemplateColumns: '1fr',
    },
  },

  heroCard: {
    backgroundColor: '#0F3D2E',
    borderRadius: '32px',
    padding: '34px',
    color: '#ffffff',
    boxShadow: '0 22px 50px rgba(15, 61, 46, 0.18)',
    border: '1px solid rgba(201, 161, 74, 0.45)',

    '@media (max-width: 520px)': {
      padding: '24px',
      borderRadius: '28px',
    },
  },

  logoBadge: {
    width: '78px',
    height: '78px',
    borderRadius: '50%',
    border: '2px solid #C9A14A',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '22px',
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.14)',
    overflow: 'hidden',

    '@media (max-width: 520px)': {
      width: '68px',
      height: '68px',
    },
  },

  logoImage: {
    width: '62px',
    height: '62px',
    objectFit: 'contain',
    display: 'block',

    '@media (max-width: 520px)': {
      width: '54px',
      height: '54px',
    },
  },

  kicker: {
    margin: '0 0 8px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },

  heroTitle: {
    margin: 0,
    fontSize: '42px',
    lineHeight: 1.06,
    letterSpacing: '-1.8px',
    fontWeight: '900',
    maxWidth: '520px',

    '@media (max-width: 520px)': {
      fontSize: '32px',
    },
  },

  heroText: {
    margin: '18px 0 28px',
    color: '#F4EBD8',
    fontSize: '15px',
    lineHeight: 1.8,
    maxWidth: '540px',
  },

  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',

    '@media (max-width: 520px)': {
      gridTemplateColumns: '1fr',
    },
  },

  featureCard: {
    backgroundColor: 'rgba(255, 253, 247, 0.08)',
    border: '1px solid rgba(230, 216, 190, 0.22)',
    borderRadius: '22px',
    padding: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  featureIcon: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: '#C9A14A',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },

  featureTitle: {
    display: 'block',
    color: '#ffffff',
    fontSize: '14px',
    marginBottom: '3px',
  },

  featureText: {
    margin: 0,
    color: '#F4EBD8',
    fontSize: '12px',
    lineHeight: 1.4,
  },

  card: {
    backgroundColor: '#ffffff',
    padding: '28px',
    borderRadius: '32px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #E6D8BE',

    '@media (max-width: 520px)': {
      padding: '22px',
      borderRadius: '28px',
    },
  },

  formHeader: {
    marginBottom: '22px',
  },

  formKicker: {
    margin: '0 0 6px',
    color: '#C9A14A',
    fontSize: '11px',
    fontWeight: '900',
    letterSpacing: '0.9px',
    textTransform: 'uppercase',
  },

  title: {
    margin: 0,
    fontSize: '30px',
    color: '#0F3D2E',
    fontWeight: '900',
    letterSpacing: '-0.8px',
  },

  subtitle: {
    margin: '8px 0 0',
    color: '#8C8170',
    fontSize: '14px',
    lineHeight: 1.6,
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
  },

  nameGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',

    '@media (max-width: 520px)': {
      gridTemplateColumns: '1fr',
      gap: 0,
    },
  },

  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
  },

  label: {
    color: '#0F3D2E',
    fontSize: '13px',
    fontWeight: '800',
    marginBottom: '7px',
  },

  input: {
    width: '100%',
    padding: '14px 15px',
    fontSize: '15px',
    borderRadius: '18px',
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    outline: 'none',
    marginBottom: '14px',
    boxSizing: 'border-box',

    ':focus': {
      borderColor: '#C9A14A',
      boxShadow: '0 0 0 3px rgba(201, 161, 74, 0.15)',
    },
  },

  button: {
    padding: '15px',
    fontSize: '15px',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    border: 'none',
    borderRadius: '20px',
    fontWeight: '900',
    marginTop: '8px',
    boxShadow: '0 10px 24px rgba(201, 161, 74, 0.2)',
    cursor: 'pointer',

    ':hover': {
      transform: 'translateY(-1px)',
    },
  },

  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },

  errorBox: {
    backgroundColor: '#FFE9E9',
    border: '1px solid #F5B8B8',
    borderRadius: '18px',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    marginBottom: '14px',
  },

  errorIcon: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: '#D9534F',
    color: '#ffffff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '900',
    flexShrink: 0,
  },

  error: {
    color: '#D9534F',
    fontSize: '13px',
    margin: 0,
    fontWeight: '700',
    lineHeight: 1.5,
  },

  links: {
    marginTop: '18px',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },

  switchText: {
    color: '#6E6254',
  },

  linkButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: '#0F3D2E',
    fontWeight: '900',
    cursor: 'pointer',
    fontSize: '14px',
    padding: 0,

    ':hover': {
      color: '#B85C38',
    },
  },
});
